import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { CancelSVG, DropDownSVG } from '../../components/svgs';
import { BUTTON_CONSTANTS, MARKETPLACE_CONSTANTS, VALIDATION_ALERT_CONSTANTS } from '../../constants/common';
import { MARKETPLACE_SPECIMEN_TYPE_FILTER_OPTIONS } from '../../types/enums/specimenType.enum';
import { Breed, Species } from '../../types/interfaces/animal.interfaces';
import { getBreeds } from '../../api/breedsApi';
import { getSpecies } from '../../api/speciesApi';
import { dollarsToCents, centsToDollars } from '../../services/account.service';
import { normalizePayload, sortByNameAlphabetically } from '../../utils/commonUtils';
import { getMarketplacePriceRange } from '../../api/marketPlaceSpecimenApi';

export type FilterSelection = {
  speciesIds?: number[];
  breedIds?: number[];
  specimenTypeIds?: number[];
  minPriceCents?: number;
  maxPriceCents?: number;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initial?: FilterSelection;
  onApply: (filters: FilterSelection) => void;
  variant?: 'modal' | 'sidebar';
  productCount?: number;
};

const MarketPlaceFilters: FC<Props> = ({ isOpen, onClose, initial, onApply, variant = 'modal', productCount }) => {
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [selectedSpecies, setSelectedSpecies] = useState<number[]>(initial?.speciesIds ?? []);
  const [selectedBreeds, setSelectedBreeds] = useState<number[]>(initial?.breedIds ?? []);
  const [selectedSpecimenTypes, setSelectedSpecimenTypes] = useState<number[]>(initial?.specimenTypeIds ?? []);
  const [isSpeciesOpen, setIsSpeciesOpen] = useState(true);
  const [isBreedOpen, setIsBreedOpen] = useState(true);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [rangeBoundsMin, setRangeBoundsMin] = useState<number>(0);
  const [rangeBoundsMax, setRangeBoundsMax] = useState<number>(1000);
  const [minRange, setMinRange] = useState<number>(0);
  const [maxRange, setMaxRange] = useState<number>(1000);
  const [minInput, setMinInput] = useState<string>('');
  const [maxInput, setMaxInput] = useState<string>('');

  const fetchedRef = useRef(false);
  const wasOpenRef = useRef(isOpen);
  const isMinEntered = minInput.trim() !== '' && !isNaN(Number(minInput));

  const minVal = Number(minInput);
  const maxVal = Number(maxInput);
  const isMaxEntered = maxInput.trim() !== '' && !isNaN(maxVal);
  const isBothValid = isMinEntered && isMaxEntered;
  const isMinLessThanRange = isMinEntered && minVal < rangeBoundsMin;
  const isMaxGreaterThanRange = isMaxEntered && maxVal > rangeBoundsMax;
  const isPriceRangeInvalid = isBothValid && maxVal <= minVal;
  const isPriceRangeMissing = !isMinEntered || !isMaxEntered;

  const isApplyDisabled = isPriceRangeMissing || isMinLessThanRange || isMaxGreaterThanRange || isPriceRangeInvalid;

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened) {
      setIsSpeciesOpen(true);
      setIsBreedOpen(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchFilterData = async () => {
      setIsOptionsLoading(true);
      try {
        const [breedsRes, speciesRes, priceRangeRes] = await Promise.all([
          getBreeds()
            .then(({ data }) => normalizePayload<Breed>(data))
            .catch(() => [] as Breed[]),
          getSpecies()
            .then(({ data }) => normalizePayload<Species>(data))
            .catch(() => [] as Species[]),
          getMarketplacePriceRange()
            .then(({ data }) => data)
            .catch(() => null),
        ]);

        setBreeds(sortByNameAlphabetically(breedsRes));
        setSpecies(sortByNameAlphabetically(speciesRes));

        if (priceRangeRes) {
          const apiMin = Number(centsToDollars(priceRangeRes.lowestUnitPriceCents));
          const apiMax = Number(centsToDollars(priceRangeRes.highestUnitPriceCents));
          const safeMax = apiMax <= apiMin ? apiMin + 100 : apiMax;
          const initialMin = initial?.minPriceCents != null ? Number(centsToDollars(initial.minPriceCents)) : apiMin;
          const initialMax = initial?.maxPriceCents != null ? Number(centsToDollars(initial.maxPriceCents)) : safeMax;

          setRangeBoundsMin(apiMin);
          setRangeBoundsMax(safeMax);
          setMinRange(initialMin);
          setMaxRange(initialMax);
          setMinInput(String(initialMin));
          setMaxInput(String(initialMax));
        }
      } finally {
        setIsOptionsLoading(false);
      }
    };

    void fetchFilterData();
  }, []);

  useEffect(() => {
    if (isMinEntered) {
      setMinRange(minVal);
    }
  }, [minInput]);

  useEffect(() => {
    if (maxInput.trim() !== '' && !isNaN(maxVal)) {
      setMaxRange(maxVal);
    }
  }, [maxInput]);

  const toggleSelection = (selectedIds: number[], valueId: number): number[] => {
    return selectedIds.includes(valueId) ? selectedIds.filter(id => id !== valueId) : [...selectedIds, valueId];
  };

  const getRangeValue = (value: number): number => {
    return Math.min(Math.max(value, rangeBoundsMin), rangeBoundsMax);
  };

  const formatPriceRangeValue = (value: number): string => {
    return `$${value.toLocaleString(undefined, {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleMinInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setMinInput(raw);
  };

  const handleMaxInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setMaxInput(raw);
  };

  const handleMinSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setMinRange(value);
    setMinInput(String(value));
    if (value > maxRange) {
      setMaxRange(value);
      setMaxInput(String(value));
    }
  };

  const handleMaxSliderChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);

    setMaxRange(value);
    setMaxInput(String(value));

    if (value < minRange) {
      setMinRange(value);
      setMinInput(String(value));
    }
  };

  const handleApply = () => {
    if (isApplyDisabled) return;
    onApply({
      speciesIds: selectedSpecies,
      breedIds: selectedBreeds,
      specimenTypeIds: selectedSpecimenTypes,
      minPriceCents: dollarsToCents(isMinEntered ? minVal : rangeBoundsMin),
      maxPriceCents: dollarsToCents(maxInput.trim() !== '' && !isNaN(maxVal) ? maxVal : rangeBoundsMax),
    });
    if (variant === 'modal') onClose();
  };

  const handleClear = () => {
    setSelectedSpecies([]);
    setSelectedBreeds([]);
    setSelectedSpecimenTypes([]);
    setMinRange(rangeBoundsMin);
    setMaxRange(rangeBoundsMax);

    setMinInput(String(rangeBoundsMin));
    setMaxInput(String(rangeBoundsMax));

    onApply({
      speciesIds: [],
      breedIds: [],
      specimenTypeIds: [],
      minPriceCents: dollarsToCents(rangeBoundsMin),
      maxPriceCents: dollarsToCents(rangeBoundsMax),
    });
  };

  const renderOptionSkeletons = () =>
    Array.from({ length: 7 }, (_, index) => <div key={`filter-skeleton-${index}`} className="filter-option-skeleton" />);

  const renderSpeciesSection = () => (
    <div className={`filter-section filter-section--collapsible ${isSpeciesOpen ? 'is-open' : 'is-closed'}`}>
      <button
        type="button"
        className="filter-section__toggle"
        onClick={() => setIsSpeciesOpen(prev => !prev)}
        aria-expanded={isSpeciesOpen}>
        <h4>
          {MARKETPLACE_CONSTANTS.SPECIES}
          {selectedSpecies.length > 0 && <span className="filter-section__count">{selectedSpecies.length}</span>}
        </h4>
        <DropDownSVG reversed={isSpeciesOpen} />
      </button>
      {isSpeciesOpen && (
        <div className={`scrollable ${isOptionsLoading ? 'is-loading' : ''}`}>
          {isOptionsLoading
            ? renderOptionSkeletons()
            : species
                .filter(speciesItem => speciesItem.speciesId != null)
                .map(speciesItem => {
                  const speciesId = speciesItem.speciesId as number;
                  return (
                    <label key={speciesId}>
                      <input
                        type="checkbox"
                        checked={selectedSpecies.includes(speciesId)}
                        onChange={() => setSelectedSpecies(toggleSelection(selectedSpecies, speciesId))}
                      />
                      {speciesItem.name}
                    </label>
                  );
                })}
        </div>
      )}
    </div>
  );

  const renderBreedSection = () => (
    <div className={`filter-section filter-section--collapsible ${isBreedOpen ? 'is-open' : 'is-closed'}`}>
      <button
        type="button"
        className="filter-section__toggle"
        onClick={() => setIsBreedOpen(prev => !prev)}
        aria-expanded={isBreedOpen}>
        <h4>
          {MARKETPLACE_CONSTANTS.BREED}
          {selectedBreeds.length > 0 && <span className="filter-section__count">{selectedBreeds.length}</span>}
        </h4>
        <DropDownSVG reversed={isBreedOpen} />
      </button>
      {isBreedOpen && (
        <div className={`scrollable ${isOptionsLoading ? 'is-loading' : ''}`}>
          {isOptionsLoading
            ? renderOptionSkeletons()
            : breeds
                .filter(breed => breed.breedId != null)
                .map(breed => {
                  const id = breed.breedId as number;
                  return (
                    <label key={id}>
                      <input
                        type="checkbox"
                        checked={selectedBreeds.includes(id)}
                        onChange={() => setSelectedBreeds(toggleSelection(selectedBreeds, id))}
                      />
                      {breed.name} {breed.species?.name ? `(${breed.species.name})` : ''}
                    </label>
                  );
                })}
        </div>
      )}
    </div>
  );

  const renderProductTypeSection = () => (
    <div className="filter-section">
      <h4>{MARKETPLACE_CONSTANTS.PRODUCT_TYPE}</h4>
      {MARKETPLACE_SPECIMEN_TYPE_FILTER_OPTIONS.map(option => (
        <label key={option.specimenTypeId}>
          <input
            type="checkbox"
            checked={selectedSpecimenTypes.includes(option.specimenTypeId)}
            onChange={() => setSelectedSpecimenTypes(toggleSelection(selectedSpecimenTypes, option.specimenTypeId))}
          />
          {option.name}
        </label>
      ))}
    </div>
  );

  const renderPriceRangeSection = () => {
    const rangeDifference = rangeBoundsMax - rangeBoundsMin || 1;
    const minRangeValue = getRangeValue(minRange);
    const maxRangeValue = getRangeValue(maxRange);

    return (
      <div className="filter-section">
        <h4>{MARKETPLACE_CONSTANTS.PRICE_RANGE}</h4>
        <div className="price-range">
          <div className="range-track">
            <div
              className="range-fill"
              style={{
                left: `${((minRangeValue - rangeBoundsMin) / rangeDifference) * 100}%`,
                right: `${100 - ((maxRangeValue - rangeBoundsMin) / rangeDifference) * 100}%`,
              }}
            />
            <input
              type="range"
              min={rangeBoundsMin}
              max={rangeBoundsMax}
              value={minRangeValue}
              onChange={handleMinSliderChange}
            />
            <input
              type="range"
              min={rangeBoundsMin}
              max={rangeBoundsMax}
              value={maxRangeValue}
              onChange={handleMaxSliderChange}
            />
          </div>
          <div className="price-inputs">
            <div className="price-input-wrapper">
              <input type="number" placeholder="Min" value={minInput} onChange={handleMinInputChange} />
            </div>
            <span className="price-separator">-</span>
            <div className="price-input-wrapper">
              <input type="number" placeholder="Max" value={maxInput} onChange={handleMaxInputChange} disabled={!isMinEntered} />
            </div>
          </div>

          {isMinLessThanRange && (
            <div className="validated-input-message-error">
              {VALIDATION_ALERT_CONSTANTS.MIN_PRICE_LESS_THAN_RANGE.replace('{minPrice}', formatPriceRangeValue(rangeBoundsMin))}
            </div>
          )}
          {isMaxGreaterThanRange && (
            <div className="validated-input-message-error">
              {VALIDATION_ALERT_CONSTANTS.MAX_PRICE_GREATER_THAN_RANGE.replace(
                '{maxPrice}',
                formatPriceRangeValue(rangeBoundsMax),
              )}
            </div>
          )}
          {isPriceRangeInvalid && (
            <div className="validated-input-message-error">{VALIDATION_ALERT_CONSTANTS.MIN_LESS_THAN_MAX}</div>
          )}
        </div>
      </div>
    );
  };

  const filterBody = () => (
    <div className="marketplace-filter-body">
      <div className="filter-header">
        <h3>{MARKETPLACE_CONSTANTS.FILTERS}</h3>
        <button type="button" className="clear" onClick={handleClear}>
          {MARKETPLACE_CONSTANTS.CLEAR_ALL}
        </button>
      </div>
      {productCount != null && (
        <p className="filter-product-count">
          {productCount} {MARKETPLACE_CONSTANTS.PRODUCTS_FOUND_SUFFIX}
        </p>
      )}

      <div className="filter-scroll">
        {renderSpeciesSection()}
        {renderBreedSection()}
        {renderProductTypeSection()}
        {renderPriceRangeSection()}
      </div>

      <div className="filter-actions">
        <button type="button" className="button green medium" onClick={handleApply} disabled={isApplyDisabled}>
          {BUTTON_CONSTANTS.APPLY}
        </button>
      </div>
    </div>
  );

  if (variant === 'sidebar') {
    return (
      <section
        className="marketplace-filter-overlay marketplace-filter-overlay--sidebar open"
        aria-label={MARKETPLACE_CONSTANTS.FILTERS}>
        <div className="marketplace-filter-panel">{filterBody()}</div>
      </section>
    );
  }

  return (
    <div
      className={`marketplace-filter-overlay ${isOpen ? 'open' : ''}`}
      aria-modal="true"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}>
      <div className="marketplace-filter-panel">
        <button type="button" className="filter-close" onClick={onClose} aria-label="Close filters">
          <CancelSVG />
        </button>
        {filterBody()}
      </div>
    </div>
  );
};

export default MarketPlaceFilters;
